import type { WidgetDefinition } from "./types";
// The buyer vocabulary lives in ONE map (buyerFilters.LABEL_OVERRIDES) and every
// render site goes through it. A dashboard that spelled a source itself would be
// the site that says "PaidSocial" while the buyer list beside it says
// "Paid social" — and the widget links straight into that list.
import { buyerLabel } from "@/pages/buyers/buyerFilters";
import { listingShortNameOrFallback } from "@/lib/listingDisplay";
import { compositeQueue, compositeStat } from "./widgets/CompositeWidgets";
import { UserManagementShortcutsWidget } from "./widgets/UserManagementShortcutsWidget";
import * as panels from "./widgets/panels";

/**
 * The frontend widget catalog: one registration per widget key, mirroring the
 * server catalog in Core/Constants/DashboardRecipes.cs. The SERVER owns
 * ordering + permission gating (per-caller claims); this map only says which
 * component renders each key. Unknown keys are skipped by the engine, so the
 * two sides can ship new widgets independently.
 *
 * "stat"/"queue" widgets render the composite GET /api/dashboard payload via
 * the generic renderers; "panel" widgets fetch their own data through
 * existing permission-gated endpoints.
 */

type Item = Record<string, unknown>;
const s = (item: Item, key: string) => {
  const v = item[key];
  return typeof v === "string" ? v : "";
};
const optS = (item: Item, key: string) => {
  const v = item[key];
  return typeof v === "string" && v !== "" ? v : null;
};
/** A numeric payload member for a queue ROW's own copy. Absent reads as 0 —
 *  every count on a row is a server-computed aggregate that is genuinely zero
 *  when the group is empty, so there is no missing-vs-zero distinction to keep. */
const n = (item: Item, key: string) => {
  const v = item[key];
  return typeof v === "number" ? v : 0;
};

/** Name → email → a marker. Never a bare id: a queue row a specialist cannot
 *  identify is worse than an ugly one, and a buyer captured from a form with
 *  only an address is an ordinary intake. A company buyer leads with the
 *  company — "{companyLabel} — {person}" — like the buyers list row
 *  (`staffDisplayName`, ruling Q5); the server sends `companyLabel` only for
 *  a company buyer. */
const buyerName = (item: Item) => {
  const name = `${s(item, "firstName")} ${s(item, "lastName")}`.trim();
  const person = name || s(item, "email") || "(no name on file)";
  const company = s(item, "companyLabel").trim();
  return company ? `${company} — ${person}` : person;
};

export const WIDGET_CATALOG: Record<string, WidgetDefinition> = {
  // ---- Composite (server-payload) widgets --------------------------------

  "new-broker-applicants": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "awaiting review",
      to: "/brokers/applications",
      emptyText: "No new applicants — nothing waiting on you.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "contactName") || s(i, "contactEmail"),
        secondary: optS(i, "contactEmail"),
        timestamp: optS(i, "submittedAt"),
      }),
    }),
  },
  "active-users": {
    component: compositeStat({ figures: [{ key: "count", label: "active" }], to: "/admin/users" }),
  },
  "audit-24h": {
    component: compositeStat({ figures: [{ key: "count", label: "events" }], to: "/admin/audit" }),
  },
  "system-health": {
    component: compositeStat({
      figures: [
        { key: "ingestionPending", label: "ingestion pending", tone: "warning" },
        { key: "ingestionFailed7d", label: "failed (7d)", tone: "error" },
      ],
    }),
  },
  "recent-role-changes": {
    component: compositeQueue({
      to: "/admin/audit",
      emptyText: "No role changes lately.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "actionDescription"),
        secondary: optS(i, "userDisplayName"),
        timestamp: optS(i, "timestamp"),
      }),
    }),
  },
  "users-without-primary-role": {
    component: compositeStat({
      figures: [{ key: "count", label: "users", tone: "warning" }],
      to: "/admin/users",
    }),
  },
  "recent-admin-undos": {
    component: compositeQueue({
      emptyText: "No admin undos executed recently.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "undoActionCode"),
        secondary: [optS(i, "dealNumber"), optS(i, "reason")].filter(Boolean).join(" — ") || null,
        timestamp: optS(i, "occurredAt"),
        href: optS(i, "dealId") ? `/pipeline/${s(i, "dealId")}` : null,
      }),
    }),
  },
  "recent-portal-account-changes": {
    component: compositeQueue({
      to: "/admin/portal-accounts",
      emptyText: "No portal accounts disabled or archived in the last 14 days.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "email"),
        badge: optS(i, "status"),
        badgeTone: s(i, "status") === "Disabled" ? "danger" : "neutral",
        timestamp: optS(i, "changedAt"),
        href: `/admin/portal-accounts/${s(i, "id")}`,
      }),
    }),
  },
  "recent-catalog-changes": {
    component: compositeQueue({
      to: "/admin/audit",
      emptyText: "No criteria or certification template changes lately.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "actionDescription"),
        secondary: optS(i, "userDisplayName"),
        badge: s(i, "entityType") === "CriteriaCatalogItem" ? "Criteria" : "Certification",
        timestamp: optS(i, "timestamp"),
      }),
    }),
  },
  "background-checks": {
    component: compositeStat({
      figures: [
        { key: "inFlight", label: "in flight", tone: "info" },
        { key: "completed7d", label: "completed (7d)", tone: "ok" },
        { key: "failed30d", label: "failed (30d)", tone: "error" },
      ],
      to: "/background-checks",
    }),
  },
  "escrow-open": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "open",
      to: "/escrow",
      emptyText: "No open escrow transactions.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "itemDescription") || s(i, "dealNumber") || "Escrow transaction",
        secondary: optS(i, "dealNumber"),
        badge: optS(i, "status"),
        badgeTone: s(i, "status") === "PendingAdminApproval" ? "warn" : "progress",
        timestamp: optS(i, "createdAt"),
        href: `/escrow/${s(i, "id")}`,
      }),
    }),
  },
  "envelopes-out": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "awaiting signature",
      to: "/collection/packages",
      emptyText: "No envelopes are out for signature.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "signerName") || s(i, "signerEmail"),
        secondary: optS(i, "signerEmail"),
        badge: optS(i, "status"),
        badgeTone: "progress",
        timestamp: optS(i, "createdAt"),
      }),
    }),
  },
  // ---- Buyer CRM (PR 2a) ------------------------------------------------
  //
  // The row builders fall back through name → email so a queue never renders a
  // blank primary line: a buyer captured from a form with only an address is a
  // normal intake, and an unnamed row a specialist cannot identify is worse
  // than an ugly one.

  "buyer-funnel": {
    component: compositeStat({
      figures: [
        { key: "total", label: "total" },
        { key: "new", label: "new", tone: "info" },
        { key: "qualified", label: "qualified", tone: "ok" },
        { key: "submitting", label: "submitting", tone: "ok" },
        { key: "converted", label: "converted", tone: "ok" },
      ],
      to: "/buyers?scope=all",
    }),
  },
  "my-buyers": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "assigned to you",
      to: "/buyers?scope=mine",
      emptyText: "No buyers assigned to you yet.",
      row: (i) => ({
        id: s(i, "id"),
        primary: buyerName(i),
        secondary: optS(i, "email"),
        timestamp: optS(i, "nextFollowUpDate"),
      }),
    }),
  },
  "buyer-assignment-queue": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "waiting",
      to: "/buyer-assignments",
      emptyText: "The queue is clear — every buyer has an owner.",
      row: (i) => ({
        id: s(i, "id"),
        primary: buyerName(i),
        secondary: optS(i, "email"),
        timestamp: optS(i, "createdAt"),
      }),
    }),
  },
  "buyer-submission-pipeline": {
    component: compositeStat({
      figures: [
        { key: "live", label: "live" },
        { key: "offerLoi", label: "offer / LOI", tone: "ok" },
        { key: "underContract", label: "under contract", tone: "ok" },
      ],
      to: "/buyer-submissions?liveOnly=1&scope=all",
    }),
  },
  "overdue-buyer-follow-ups": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "overdue",
      to: "/buyers?scope=mine&card=overdue",
      emptyText: "Nothing overdue — you are on top of your follow-ups.",
      row: (i) => ({
        id: s(i, "id"),
        primary: buyerName(i),
        secondary: optS(i, "email"),
        timestamp: optS(i, "nextFollowUpDate"),
      }),
    }),
  },

  // ---- Buyer distribution (PR 2b) ---------------------------------------
  //
  // The progress pair leads: "N toward your next" beside "M needed" is the
  // fraction, and the threshold rides the payload rather than a label here
  // because it is configurable per specialist. Every number in both widgets
  // will legitimately read zero for a while — a qualifying submission is
  // counted from the package-sent stamp, which nothing writes yet — so the
  // server descriptor's subtitle says so. Zero because none were sent is a
  // fact about the work; a card that cannot say which zero it means is what
  // reads as broken.

  "buyer-entitlement-progress": {
    component: compositeStat({
      figures: [
        { key: "towardNext", label: "toward your next" },
        { key: "qualifyingThreshold", label: "needed" },
        { key: "availableNow", label: "you can take now", tone: "ok" },
        { key: "entitlement", label: "earned total" },
        { key: "currentlyHeld", label: "held now" },
      ],
      to: "/buyers?scope=mine",
    }),
  },
  "buyer-allocation-standing": {
    component: compositeStat({
      figures: [
        { key: "eligibleSpecialists", label: "consultants eligible", tone: "ok" },
        { key: "openCapacity", label: "buyers they can take" },
        { key: "queueDepth", label: "unassigned", tone: "warning" },
        { key: "staleCount", label: "stale", tone: "error" },
      ],
      to: "/buyer-assignments",
    }),
  },

  // ---- Buyer dashboards (PR 3) ------------------------------------------
  //
  // Number first, label under it — the shared `compositeStat` shape, so every
  // figure on these cards reads the way every other figure on the page does.
  // Tone is reserved for STATE (a status token), never for a quantity: a
  // conversion percentage and a headcount are readings, not conditions, so they
  // stay onyx. Gold is the brand and never signals state, which is why the
  // badges below are `neutral` rather than `progress`.
  //
  // Every tile links to the surface that answers the question it raises — the
  // submissions list scoped the way the tile was, the buyer list filtered to the
  // source whose row you clicked, the buyer's own Associated listings tab.

  "buyer-submissions-this-period-mine": {
    component: compositeStat({
      figures: [
        { key: "started", label: "started" },
        { key: "live", label: "live" },
        { key: "offerLoi", label: "offer / LOI", tone: "ok" },
        { key: "underContract", label: "under contract", tone: "ok" },
        { key: "escalated", label: "escalated", tone: "ok" },
      ],
      to: "/buyer-submissions?scope=mine",
    }),
  },
  "buyer-submissions-this-period-all": {
    component: compositeStat({
      figures: [
        { key: "started", label: "started" },
        { key: "live", label: "live" },
        { key: "offerLoi", label: "offer / LOI", tone: "ok" },
        { key: "underContract", label: "under contract", tone: "ok" },
        { key: "escalated", label: "escalated", tone: "ok" },
      ],
      to: "/buyer-submissions?scope=all",
    }),
  },
  "buyer-conversion-rates": {
    component: compositeStat({
      // The rates lead, and the counts they were computed from follow — a lone
      // "33%" cannot say whether it came from three buyers or three hundred, and
      // the manager reading it is deciding how to staff.
      figures: [
        { key: "qualifiedRate", label: "% qualified" },
        { key: "convertedRate", label: "% converted" },
        { key: "assigned", label: "assigned buyers" },
        { key: "qualified", label: "qualified" },
        { key: "converted", label: "converted" },
      ],
      to: "/buyers?scope=all",
    }),
  },
  "buyer-conversion-by-source": {
    component: compositeQueue({
      to: "/buyers?scope=all",
      // Names the population, because the card counts ASSIGNED buyers — the same
      // denominator the Conversion card uses, so the two reconcile. Buyers still
      // sitting in the unassigned queue are not missing data here; they have not
      // had the chance to convert yet.
      emptyText: "No buyers assigned yet — this fills in as the queue is worked.",
      row: (i) => ({
        id: s(i, "source"),
        primary: buyerLabel(s(i, "source")),
        secondary: `${n(i, "total")} buyers · ${n(i, "qualified")} qualified · ${n(
          i,
          "converted",
        )} converted`,
        badge: `${n(i, "qualifiedRate")}% qualified`,
        badgeTone: "neutral",
        href: `/buyers?source=${encodeURIComponent(s(i, "source"))}&scope=all`,
      }),
    }),
  },
  "buyer-per-specialist-volume": {
    component: compositeQueue({
      to: "/buyers/allocation",
      emptyText: "No consultants on the roster yet — assign buyers to see volume here.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "name"),
        // 🔴 The self-attested figure is a SUBSET of packages sent, and is
        // rendered as one ("N packages sent, M sent themselves") rather than as
        // a second total beside it. It is also NOT subtracted to show a Phoenix
        // figure: packages sent before the marker column existed are recorded as
        // neither, so the remainder is "Phoenix-sent or unrecorded" and printing
        // it as observed would invent history. Suppressed entirely at zero, so a
        // roster where nobody uses their own mailbox stays legible.
        secondary: `${n(i, "submissionsInWindow")} started · ${n(
          i,
          "packagesSentInWindow",
        )} packages sent${
          n(i, "selfAttestedInWindow") > 0
            ? `, ${n(i, "selfAttestedInWindow")} sent themselves`
            : ""
        }`,
        badge: `${n(i, "currentlyHeld")} held`,
        badgeTone: "neutral",
      }),
    }),
  },
  "buyer-associations-not-yet-worked": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "not yet worked",
      to: "/buyers?scope=mine",
      emptyText:
        "Every listing you have shortlisted already has a submission — add more from a buyer's Associated listings tab.",
      row: (i) => ({
        id: s(i, "id"),
        // The ONE ladder, resolved server-side. A shortlisted BLIND listing has
        // no business name by design, so coalescing that column alone rendered
        // a placeholder for exactly the rows this queue exists to surface.
        //
        // SHORT: a widget row is one line beside a buyer name and a timestamp.
        primary: listingShortNameOrFallback(s(i, "listingDisplayName")),
        secondary:
          `${s(i, "buyerFirstName")} ${s(i, "buyerLastName")}`.trim() || "(no name on file)",
        timestamp: optS(i, "createdAt"),
        href: `/buyers/${s(i, "buyerLeadId")}?tab=associated-listings`,
      }),
    }),
  },

  "lead-funnel": {
    component: compositeStat({
      figures: [
        { key: "total", label: "total" },
        { key: "new", label: "new", tone: "info" },
        { key: "contacted", label: "contacted" },
        { key: "interested", label: "interested", tone: "warning" },
        { key: "handedOff", label: "handed off", tone: "ok" },
      ],
      to: "/leads",
    }),
  },
  "unassigned-brokers": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "unassigned",
      to: "/brokers",
      emptyText: "Every broker has a liaison.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "companyName") || s(i, "name"),
        secondary: optS(i, "name"),
        timestamp: optS(i, "createdAt"),
        href: `/brokers/${s(i, "id")}`,
      }),
    }),
  },
  "marketer-performance": {
    component: compositeQueue({
      to: "/leads",
      emptyText: "No marketer activity to report yet.",
      row: (i) => ({
        id: s(i, "userId"),
        primary: s(i, "displayName"),
        secondary: `${Number(i.total ?? 0)} leads · ${Number(i.contacted ?? 0)} contacted · ${Number(i.handedOff ?? 0)} handed off`,
      }),
    }),
  },
  "active-closings": {
    component: compositeQueue({
      countKey: "count",
      countLabel: "in closing",
      to: "/deal-flow/pipeline",
      emptyText: "No deals in closing — view the full pipeline.",
      row: (i) => ({
        id: s(i, "id"),
        primary: s(i, "name") || s(i, "dealNumber"),
        secondary: optS(i, "dealNumber"),
        timestamp: optS(i, "stageEnteredDate"),
        href: `/pipeline/${s(i, "id")}`,
      }),
    }),
  },
  "vendor-program": {
    component: compositeStat({
      figures: [
        { key: "activeCount", label: "active vendors" },
        { key: "added30d", label: "added (30d)", tone: "info" },
      ],
      to: "/portfolio-admin/vendors",
    }),
  },
  "enforcer-engagements": {
    component: compositeStat({
      figures: [{ key: "openCount", label: "open", tone: "warning" }],
    }),
  },
  "investor-snapshot": {
    component: compositeStat({
      figures: [
        { key: "count", label: "investors" },
        { key: "added30d", label: "added (30d)", tone: "info" },
      ],
      to: "/investors",
    }),
  },

  // ---- Static link cards ---------------------------------------------------

  "user-management-shortcuts": { component: UserManagementShortcutsWidget },

  // ---- Client-fetching panels -----------------------------------------------

  "intake-kanban": { component: panels.IntakeKanbanWidget },
  "preapproval-kanban": { component: panels.PreApprovalKanbanWidget },
  "newly-assigned-deals": { component: panels.NewlyAssignedDealsWidget },
  "ready-to-submit": { component: panels.ReadyToSubmitWidget },
  "deal-aging-mine": { component: panels.DealAgingMineWidget },
  "deal-aging-team": { component: panels.DealAgingTeamWidget },
  "recently-created-deals": { component: panels.RecentlyCreatedDealsWidget },
  "workload-distribution": { component: panels.WorkloadDistributionWidget },
  "stage-funnel": { component: panels.StageFunnelWidget },
  "my-loans": { component: panels.MyLoansWidget },
  "compliance-work-queue": { component: panels.ComplianceWorkQueueWidget },
  "qa-work-queue": { component: panels.QaWorkQueueWidget },
  "defaults-work-queue": { component: panels.DefaultsWorkQueueWidget },
  "vfi-dashboard": { component: panels.VfiDashboardWidget },
  "reporting-overview": { component: panels.ReportingOverviewWidget },
  "loan-tape-status": { component: panels.LoanTapeStatusWidget },
  "collections-outstanding": { component: panels.CollectionsOutstandingWidget },
  "my-recent-reports": { component: panels.MyRecentReportsWidget },
  "expiring-credentials": { component: panels.ExpiringCredentialsWidget },
  "new-prospects": { component: panels.NewProspectsWidget },
  "in-onboarding": { component: panels.InOnboardingWidget },
  "todays-tasks": { component: panels.TodaysTasksWidget },
  "hot-leads": { component: panels.HotLeadsWidget },
  "new-leads": { component: panels.NewLeadsWidget },
  territories: { component: panels.TerritoriesWidget },
  "pipeline-snapshot": { component: panels.PipelineSnapshotWidget },
  "analyst-load": { component: panels.AnalystLoadWidget },
  "portfolio-snapshot": { component: panels.PortfolioSnapshotWidget },
  "capital-markets-tile": { component: panels.CapitalMarketsTileWidget },
  "my-underwriting-queue": { component: panels.MyUnderwritingQueueWidget },
  "finance-earnings": { component: panels.FinanceEarningsBySourceWidget },
  "finance-outflows": { component: panels.FinanceOutflowsByRecipientWidget },
};
